import Edit16 from '@carbon/icons-react/es/edit/16';
import UserAvatar20 from '@carbon/icons-react/es/user--avatar/20';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as RsComponents, GroupProps } from 'react-select';
import styled from 'styled-components';
import { ProjectId, User } from 'api/models';
import { Button } from 'components/atoms/button';
import { CollapseIcon } from 'components/atoms/collapse/collapse-icon';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { ClearIndicator, Control, UserTag } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { DropdownIndicator } from 'components/atoms/select/popover/trigger-components';
import { AvatarOption, Picker } from 'components/molecules/squad-picker/picker';
import { SquadPopover } from 'components/molecules/squad-picker/popover';
import { Trigger } from 'components/molecules/squad-picker/trigger';
import { Option } from 'components/molecules/squad-picker/types';
import {
  optionsToSquadPayload,
  userOption,
} from 'components/molecules/squad-picker/utils';
import {
  useGetAllActiveUsersMap,
  useGetAllMembersInProject,
} from 'hooks/use-idb';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { isChildNode } from 'utils/is-child-node';
import { CK_MENTION_POPUP } from 'vendor/ckeditor/react-components/mention';

interface SuAssigneePickerProps {
  projectId: ProjectId;
  isPrivate?: boolean;
  onChange: (user: User[]) => void;
  value: Option[];
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${Button} {
    padding-right: var(--spacing-02);
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  padding: var(--spacing-03);
  background-color: var(--white-100);
`;

const DoneButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const FlexWrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
`;

const Edit = styled(Edit16)`
  color: var(--blue-60);
  margin-left: var(--spacing-03);
  display: none;
`;

const TriggerButton = styled(Button)`
  padding-left: 0;
  padding-right: 0;
  .ant-tag {
    cursor: inherit;
  }

  &:hover {
    & > ${Edit} {
      display: block;
    }
  }
`;

const Group = (
  props: GroupProps<any, false>,
  onSelectAll: () => void,
  allSelected: boolean,
) => {
  const { children, label, ...rest } = props;
  const [isVisible, setIsVisible] = useState(true);
  const toggleState = () => {
    setIsVisible((prev) => !prev);
  };
  const translate = useTranslate();

  return (
    <RsComponents.Group
      {...rest}
      label={
        <Header onClick={toggleState}>
          <FlexWrapper>
            <CollapseIcon className={isVisible ? 'active' : ''} />
            {translate('su.users.in.project')}
          </FlexWrapper>

          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              onSelectAll();
            }}
          >
            {allSelected ? (
              <FormattedMessage id="clear_all" />
            ) : (
              <FormattedMessage id="select_all" />
            )}
          </Button>
        </Header>
      }
    >
      {isVisible && children}
    </RsComponents.Group>
  );
};

export const SUAssigneePicker: FC<SuAssigneePickerProps> = (props) => {
  const { projectId, value: users = [], isPrivate = false, onChange } = props;
  const [isVisible, setVisiblity] = useState(false);
  const [, _projectId] = projectId;
  const choose = useTranslation('choose');
  const userLabel = useTranslation('su.users.in.project');
  const [value, setValue] = useState<Option[]>(users);

  const userMap = useGetAllActiveUsersMap();

  const usersInProject = useGetAllMembersInProject({
    projectId: projectId?.[1],
    isPrivate,
  });

  const userOptions = useMemo(
    () => [
      {
        label: userLabel,
        options: usersInProject.map((u) => userOption(u)),
      },
    ],
    [usersInProject, userLabel],
  );

  const inProjectUserIds = useMemo(
    () => usersInProject.map((user) => user.userId),
    [usersInProject],
  );

  const selectedUserIds = useMemo(
    () => value.map((_usr) => Number(_usr.value.split('_').at(-1))),
    [value],
  );

  const selectAll = () => {
    if (value.length !== userOptions[0].options.length) {
      setValue(userOptions[0].options);

      return;
    }
    setValue([]);
  };

  const allSelected = inProjectUserIds.every((_userId) => {
    return selectedUserIds.includes(_userId);
  });

  const onDoneFn = () => {
    setVisiblity(false);
    const { users: usr = [] } = optionsToSquadPayload(value as Option[]);
    const selectedUsers = usr
      .map(({ userId }) => userMap[userId])
      .filter(Boolean);

    onChange(selectedUsers);
  };

  const onSelectChange = useCallback(
    (e) => {
      const { users: usr = [] } = optionsToSquadPayload(e as Option[]);
      const selectedUsers = usr
        .map(({ userId }) => userMap[userId])
        .filter(Boolean);

      onChange(selectedUsers);
      setValue(e as Option[]);
    },
    [onChange, userMap],
  );

  const isChild = isChildNode([
    'su-assignee-picker',
    'assignee-picker-hosted-component',
    'su-publish-modal',
    CK_MENTION_POPUP,
  ]);

  const onOutsideClick = () => setVisiblity(false);

  return (
    <OutsideClickHandler allowed={isChild} onOutsideClick={onOutsideClick}>
      <SquadPopover
        visible={isVisible}
        placement="bottomLeft"
        content={
          <div className="su-assignee-picker">
            <Picker
              onChange={onSelectChange}
              value={value}
              options={userOptions}
              components={{
                IndicatorSeparator: null,
                DropdownIndicator,
                Control,
                ClearIndicator,
                Group: (_props) => Group(_props, selectAll, allSelected),
                Option: AvatarOption,
                MenuList: VirtualizedMenuList,
              }}
            />
            <ButtonWrapper>
              <DoneButton
                type="tertiary"
                onClick={onDoneFn}
                data-cy="assignee.picker.popup.done"
              >
                <FormattedMessage id="done" />
              </DoneButton>
            </ButtonWrapper>
          </div>
        }
      >
        <TriggerButton
          type={value.length > 0 ? 'text' : 'link'}
          size="small"
          icon={value.length > 0 ? null : <UserAvatar20 />}
          onClick={() => setVisiblity(true)}
        >
          {allSelected ? (
            <UserTag label="All project members" />
          ) : value.length > 0 ? (
            <Trigger size="small" value={value} />
          ) : (
            choose
          )}
          {value.length > 0 && <Edit />}
        </TriggerButton>
      </SquadPopover>
    </OutsideClickHandler>
  );
};
